/**
 * @page /portal/abwesend
 * @description Mobile Portal — Abwesenheiten: Meine Liste, Neuer Antrag (alle Typen inkl. Urlaub/ZA),
 *   Team-Kalender. Synchronized with Desktop PortalAbwesenheiten (v2.293.0).
 * @features
 *   - KPI-Cards: Urlaubsanspruch, Verbraucht, Saldo, Offene Anträge
 *   - Meine Tab: Card-Liste aller Abwesenheiten mit Status/Storno
 *   - Antrag Tab: 2-Step Wizard (Typ-Auswahl → Formular) — alle Typen inline, kein Redirect
 *   - Saldo-Prognose bei Urlaub/ZA zum Antritts-Datum
 *   - Halber-Tag-Toggle für Urlaub/ZA
 *   - Datei-Upload für Krankenstand (Ärztliche Bestätigung)
 *   - Team Tab: Monatskalender mit Abwesenheits-Dots
 * @api GET /api/portal/urlaub/kontingent, GET /api/portal/meine-abwesenheiten
 * @api GET /api/abwesenheitsarten, POST /api/portal/urlaub/antraege, POST /api/portal/abwesenheit
 * @api PUT /api/portal/abwesenheit/:id/storno, PUT /api/portal/urlaub/antraege/:id/storno
 * @api GET /api/portal/urlaub/kalender, GET /api/urlaub/berechne-arbeitstage
 * @api PUT /api/portal/krankmeldung/:id/dokument
 * @roles portal_mitarbeiter
 * @i18n mobilePortal.abwesend
 * @since v2.243.0, synced v2.293.0
 */
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import * as LucideIcons from 'lucide-react';
import { berechnePlanbarAnteile, naechsterUjStart } from '../../utils/urlaubPlanbar';

const API = import.meta.env.VITE_API_URL || '';

// ─── Status badge colors ──────────────────────────────────────
const STATUS_COLORS = {
  beantragt: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  genehmigt: { bg: 'bg-[#338edc]/10', text: 'text-[#338edc]', border: 'border-[#338edc]/20' },
  aktiv: { bg: 'bg-[#338edc]/10', text: 'text-[#338edc]', border: 'border-[#338edc]/20' },
  abgelehnt: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  storniert: { bg: 'bg-[var(--bg-inset,#F9FAFB)]', text: 'text-[var(--text-tertiary,#6B7280)]', border: 'border-[var(--border-default,#E5E7EB)]' },
  abgeschlossen: { bg: 'bg-[var(--bg-inset,#F0F9FF)]', text: 'text-[#338edc]', border: 'border-[#338edc]/20' },
};

// ─── Team calendar legend colors ──────────────────────────────
const TEAM_COLORS = {
  urlaub: '#338edc',
  krankenstand: '#EF4444',
  zeitausgleich: '#F59E0B',
  sonstige: '#9CA3AF',
};

// ─── Helpers ──────────────────────────────────────────────────
function fmtDate(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).slice(0, 10); // YYYY-MM-DD (handles ISO timestamps too)
  const d = new Date(s + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// #1771: Jahresanspruch ohne unnötige ",0" (25 statt 25,0), Halbtage bleiben sichtbar.
function fmtTage(n) {
  return Number(n).toLocaleString('de-AT', { maximumFractionDigits: 1 });
}

function fmtRange(von, bis) {
  if (!von) return '';
  if (!bis || bis === von) return fmtDate(von);
  return `${fmtDate(von)} – ${fmtDate(bis)}`;
}

function toMonthStr(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

// ─── Urlaub/ZA type check ────────────────────────────────────
function isUrlaubType(key) {
  return key === 'urlaub' || key === 'zeitausgleich';
}

export default function MobileAbwesend() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Tabs ─────────────────────────────────────────────────
  const tabs = ['meine', 'antrag'];
  const typParam = searchParams.get('typ');
  const [activeTab, setActiveTab] = useState(typParam ? 'antrag' : (searchParams.get('tab') || 'meine'));

  // ─── KPI/Kontingent state ───────────────────────────────
  const [kontingent, setKontingent] = useState(null);

  // ─── Meine Tab state ──────────────────────────────────────
  const [abwesenheiten, setAbwesenheiten] = useState([]);
  const [loadingMeine, setLoadingMeine] = useState(false);
  const [stornoId, setStornoId] = useState(null);

  // ─── Antrag Tab state ─────────────────────────────────────
  const [arten, setArten] = useState([]);
  const [loadingArten, setLoadingArten] = useState(false);
  const [pendingArtKey, setPendingArtKey] = useState(null);
  const [antragStep, setAntragStep] = useState(1);
  const [selectedArt, setSelectedArt] = useState(null);
  const [vonDatum, setVonDatum] = useState('');
  const [bisDatum, setBisDatum] = useState('');
  const [halberTag, setHalberTag] = useState(false);
  const [grund, setGrund] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [arbeitstage, setArbeitstage] = useState(null);
  const [projectedSaldo, setProjectedSaldo] = useState(null);
  // Krankmeldung: offenes Bis-Datum
  const [krankDauer, setKrankDauer] = useState('unbekannt'); // 'unbekannt' | 'bis'

  // ─── Team Tab state ───────────────────────────────────────
  const [teamData, setTeamData] = useState({});
  const [teamMonth, setTeamMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [namenSichtbar, setNamenSichtbar] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // Tab switching
  // ═══════════════════════════════════════════════════════════
  function switchTab(tab) {
    setActiveTab(tab);
    setSearchParams({ tab });
    setSuccessMsg('');
    setErrorMsg('');
    if (tab === 'antrag') {
      setAntragStep(1);
      setSelectedArt(null);
      setVonDatum('');
      setBisDatum('');
      setHalberTag(false);
      setGrund('');
      setUploadFile(null);
      setArbeitstage(null);
      setProjectedSaldo(null);
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Fetch: Kontingent (KPIs)
  // ═══════════════════════════════════════════════════════════
  async function fetchKontingent() {
    try {
      const res = await fetch(`${API}/api/portal/urlaub/kontingent`, { credentials: 'include' });
      if (res.ok) setKontingent(await res.json());
    } catch { /* ignore */ }
  }

  // ═══════════════════════════════════════════════════════════
  // Fetch: Meine Abwesenheiten
  // ═══════════════════════════════════════════════════════════
  async function fetchMeine() {
    setLoadingMeine(true);
    try {
      const res = await fetch(`${API}/api/portal/meine-abwesenheiten`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setAbwesenheiten(json.data || []);
      }
    } catch { /* ignore */ }
    setLoadingMeine(false);
  }

  // ═══════════════════════════════════════════════════════════
  // Fetch: Abwesenheitsarten
  // ═══════════════════════════════════════════════════════════
  async function fetchArten() {
    setLoadingArten(true);
    try {
      const res = await fetch(`${API}/api/portal/abwesenheitsarten`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setArten(json.data || []);
      }
    } catch { /* ignore */ }
    setLoadingArten(false);
  }

  // ═══════════════════════════════════════════════════════════
  // Fetch: Team Kalender
  // ═══════════════════════════════════════════════════════════
  async function fetchTeam() {
    setLoadingTeam(true);
    try {
      const monatStr = toMonthStr(teamMonth.year, teamMonth.month);
      const res = await fetch(`${API}/api/portal/urlaub/kalender?monat=${monatStr}`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setTeamData(json.tage || {});
        setNamenSichtbar(json.namen_sichtbar || false);
      }
    } catch { /* ignore */ }
    setLoadingTeam(false);
  }

  // ═══════════════════════════════════════════════════════════
  // Storno — use correct endpoint per source
  // ═══════════════════════════════════════════════════════════
  async function handleStorno(item) {
    setStornoId(item.id);
    try {
      const url = item.quelle === 'urlaub_antraege'
        ? `${API}/api/portal/urlaub/antraege/${item.id}/storno`
        : `${API}/api/portal/abwesenheit/${item.id}/storno`;
      const res = await fetch(url, { method: 'PUT', credentials: 'include' });
      if (res.ok) {
        setAbwesenheiten(prev => prev.map(a => a.id === item.id ? { ...a, status: 'storniert' } : a));
        fetchKontingent();
      }
    } catch { /* ignore */ }
    setStornoId(null);
  }

  // ═══════════════════════════════════════════════════════════
  // Check if type is Krankenstand (uses new open-ended concept)
  // ═══════════════════════════════════════════════════════════
  function isKrankenstandArt(key) {
    return key === 'krankenstand';
  }

  // ═══════════════════════════════════════════════════════════
  // Submit Antrag — different endpoint for Urlaub/ZA, Krankenstand, others
  // ═══════════════════════════════════════════════════════════
  async function handleSubmitAntrag(e) {
    e.preventDefault();
    if (!selectedArt) return;
    setSubmitting(true);
    setErrorMsg('');

    try {
      if (isKrankenstandArt(selectedArt.key)) {
        // Krankenstand → POST /api/portal/krankmeldung (new concept: open-ended)
        const res = await fetch(`${API}/api/portal/krankmeldung`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notiz: grund || undefined,
            bis_voraussichtlich: krankDauer === 'bis' && bisDatum ? bisDatum : undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('mobilePortal.abwesend.antragError'));

        // Upload file if provided
        if (uploadFile && data?.data?.id) {
          const fd = new FormData();
          fd.append('file', uploadFile);
          const up = await fetch(`${API}/api/portal/krankmeldung/${data.data.id}/dokument?src=urlaub_antraege`, {
            method: 'PUT',
            credentials: 'include',
            body: fd,
          }).catch(() => null);
          if (!up || !up.ok) throw new Error(t('mobilePortal.abwesend.uploadError', 'Krankmeldung gespeichert, aber der Nachweis konnte nicht hochgeladen werden. Bitte erneut hochladen.'));
        }
      } else if (isUrlaubType(selectedArt.key)) {
        // Urlaub/ZA → POST /api/portal/urlaub/antraege
        if (!vonDatum) return;
        const res = await fetch(`${API}/api/portal/urlaub/antraege`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            von: vonDatum,
            bis: halberTag ? vonDatum : (bisDatum || vonDatum),
            halber_tag: halberTag,
            notiz: grund || undefined,
            typ: selectedArt.key,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('mobilePortal.abwesend.antragError'));
      } else {
        // All other types → POST /api/portal/abwesenheit
        if (!vonDatum) return;
        const res = await fetch(`${API}/api/portal/abwesenheit`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            art_key: selectedArt.key,
            von_datum: vonDatum,
            bis_datum: bisDatum || vonDatum,
            grund: grund || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('mobilePortal.abwesend.antragError'));
        // #1785: Kein Nachweis-Upload in diesem Zweig — data.data.id ist eine
        //   abwesenheiten-ID, der Endpoint erwartet eine urlaub_antraege-ID.
        //   Krankenstand laeuft ohnehin oben in den Krankmeldungs-Zweig.
      }

      setAntragStep(1);
      setSelectedArt(null);
      setVonDatum('');
      setBisDatum('');
      setHalberTag(false);
      setGrund('');
      setUploadFile(null);
      setProjectedSaldo(null);
      setKrankDauer('unbekannt');
      switchTab('meine');
      // Set success AFTER switchTab (which clears messages)
      setSuccessMsg(t('mobilePortal.abwesend.antragSuccess'));
      fetchMeine();
      fetchKontingent();
    } catch (err) {
      setErrorMsg(err.message || t('mobilePortal.abwesend.antragError'));
    }
    setSubmitting(false);
  }

  // ═══════════════════════════════════════════════════════════
  // Effects
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    fetchKontingent();
  }, []);

  // ─── Handle ?typ= param from quick actions (MobileHome) ───
  useEffect(() => {
    if (!typParam) return;
    const today = new Date().toISOString().slice(0, 10);

    if (typParam === 'urlaub') {
      setSelectedArt({ key: 'urlaub', label_de: 'Urlaub', label_en: 'Vacation', farbe: '#338edc', icon: 'Palmtree', braucht_genehmigung: true });
      setAntragStep(2);
    } else if (typParam === 'krank') {
      setPendingArtKey('krankenstand');
      setVonDatum(today);
    }
    // Clean typ from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('typ');
    newParams.set('tab', 'antrag');
    setSearchParams(newParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-select pending art after arten load ─────────────
  useEffect(() => {
    if (!pendingArtKey || arten.length === 0) return;
    const matched = arten.find(a => a.key === pendingArtKey);
    if (matched) {
      setSelectedArt(matched);
      setAntragStep(2);
    }
    setPendingArtKey(null);
  }, [arten, pendingArtKey]);

  useEffect(() => {
    if (activeTab === 'meine') { fetchMeine(); }
    if (activeTab === 'antrag') fetchArten();
    if (activeTab === 'team') fetchTeam();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'team') fetchTeam();
  }, [teamMonth.year, teamMonth.month]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-calculate Arbeitstage ────────────────────────────
  useEffect(() => {
    if (halberTag) { setArbeitstage(0.5); return; }
    if (!vonDatum) { setArbeitstage(null); return; }
    const bis = bisDatum || vonDatum;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/urlaub/berechne-arbeitstage?von=${vonDatum}&bis=${bis}`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          const json = await res.json();
          setArbeitstage(json.tage ?? 0);
        }
      } catch { if (!cancelled) setArbeitstage(0); }
    })();
    return () => { cancelled = true; };
  }, [vonDatum, bisDatum, halberTag]);

  // ─── Projected saldo for Urlaub/ZA (v2.289.0 sync) ────────
  useEffect(() => {
    if (!vonDatum || !selectedArt || !isUrlaubType(selectedArt.key)) {
      setProjectedSaldo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/portal/urlaub/kontingent?stichtag=${vonDatum}`, { credentials: 'include' });
        if (!cancelled && res.ok) setProjectedSaldo(await res.json());
      } catch { if (!cancelled) setProjectedSaldo(null); }
    })();
    return () => { cancelled = true; };
  }, [vonDatum, selectedArt]);

  // ═══════════════════════════════════════════════════════════
  // Team calendar helpers
  // ═══════════════════════════════════════════════════════════
  function getDaysInMonth(y, m) {
    return new Date(y, m, 0).getDate();
  }

  function getFirstDayOfWeek(y, m) {
    const d = new Date(y, m - 1, 1).getDay();
    return d === 0 ? 6 : d - 1;
  }

  function prevMonth() {
    setTeamMonth(prev => {
      if (prev.month === 1) return { year: prev.year - 1, month: 12 };
      return { ...prev, month: prev.month - 1 };
    });
  }

  function nextMonth() {
    setTeamMonth(prev => {
      if (prev.month === 12) return { year: prev.year + 1, month: 1 };
      return { ...prev, month: prev.month + 1 };
    });
  }

  function getTypColor(typ) {
    if (!typ) return TEAM_COLORS.sonstige;
    const lower = typ.toLowerCase();
    if (lower.includes('urlaub') && !lower.includes('unbezahlt')) return TEAM_COLORS.urlaub;
    if (lower.includes('krank')) return TEAM_COLORS.krankenstand;
    if (lower.includes('zeitausgleich') || lower === 'za') return TEAM_COLORS.zeitausgleich;
    return TEAM_COLORS.sonstige;
  }

  // Check if Krankenstand type (needs file upload)
  function isKrankenstandType(art) {
    return art?.key === 'krankenstand' || art?.benoetigt_bestaetigung_ab_tage;
  }

  // ═══════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen pb-6" style={{ background: 'var(--bg-page, #F8F9FC)' }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
          {t('mobilePortal.abwesend.title')}
        </h1>
      </div>

      {/* #1769: vereinfachte MA-Ansicht — Hero (planbar bis UJ-Ende) + 2 Kontext-Cards.
          Reine Anzeige aus dem Kontingent-Response, keine neue Berechnung. */}
      {kontingent && (() => {
        const fmt1 = n => Number(n).toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        // planbar = bestehende verfuegbar_planbar-Zahl (voller Anspruch nach
        // 6M inkl. Vorgriff, minus genommen/geplant im UJ) — NICHT neu gerechnet.
        const { planbar, realVerfuegbar, vorgriffAnteil } = berechnePlanbarAnteile(kontingent);
        const standHeute = Number(kontingent.aktuell_verfuegbar ?? kontingent.saldo ?? 0);
        // #1769: verplant zeigt NUR das laufende Urlaubsjahr; der Rest wird als
        // Hinweis ausgewiesen statt den Saldo des laufenden UJ zu belasten.
        const geplantGesamt = Number(kontingent.geplant ?? 0);
        const verplantUj = Number(kontingent.geplant_uj ?? kontingent.geplant ?? 0);
        const naechstesUj = Math.round(Math.max(0, geplantGesamt - verplantUj) * 100) / 100;
        // #1748: Überplanung kommt aus dem SSoT-Payload; Fallback rechnet sie aus
        // den Anzeige-Feldern nach, damit die Warnung nie stillschweigend entfällt.
        const up = kontingent.ueberplanung || null;
        const freiFallback = kontingent.verfuegbar_nach_geplant != null
          ? Number(kontingent.verfuegbar_nach_geplant)
          : Math.round((standHeute - verplantUj) * 100) / 100;
        const rawPlanbar = up
          ? Number(up.raw_planbar)
          : Math.round((freiFallback + Number(kontingent.vorgriff_effektiv ?? 0)) * 100) / 100;
        const ueberzogenUm = up ? Number(up.ueberzogen_um) : Math.max(0, Math.round(-rawPlanbar * 100) / 100);
        const istUeberplant = ueberzogenUm > 0;
        const skala = Math.max(planbar, 0.01);
        const pctReal = Math.min(100, (Math.max(0, realVerfuegbar) / skala) * 100);
        const pctVorgriff = Math.min(100 - pctReal, (vorgriffAnteil / skala) * 100);
        const vorgriffPattern = 'repeating-linear-gradient(45deg, #8fc3ec, #8fc3ec 4px, rgba(143,195,236,0.35) 4px, rgba(143,195,236,0.35) 8px)';
        // #1772: HR kann jede Card pro MA ausblenden (Default AN). Reine Sicht-
        // barkeit — Werte und die harte Antrags-Validierung bleiben unberührt.
        const zeigePlanbar = kontingent.portal_zeige_planbar !== false;
        const zeigeSaldo = kontingent.portal_zeige_saldo !== false;
        const zeigeVerplant = kontingent.portal_zeige_verplant !== false;
        // #1808: Light-Modus blendet NUR die grafische Aufteilung aus (Balken + Legende
        // Guthaben/Vorgriff). Die Gesamtzahl „Zum Planen verfuegbar" bleibt sichtbar.
        const lightModus = kontingent.app_light_modus === true;
        if (!zeigePlanbar && !zeigeSaldo && !zeigeVerplant) return null;
        return (
          <div className="px-4 mb-4">
            {/* #1748: Überplanungs-Banner — geplant übersteigt Saldo + zulässigen Vorgriff */}
            {zeigePlanbar && istUeberplant && (
              <div className="rounded-2xl border px-4 py-3 mb-2"
                style={up && up.severity === 'fehler'
                  ? { background: '#FEE2E2', borderColor: '#FECACA' }
                  : { background: '#FEF3C7', borderColor: '#FDE68A' }}>
                <p className="text-[12px] leading-snug" style={{ color: up && up.severity === 'fehler' ? '#DC2626' : '#92400E' }}>
                  {t('mobilePortal.abwesend.ueberplantBanner', {
                    tage: fmt1(ueberzogenUm),
                    defaultValue: 'Achtung: Du hast {{tage}} Tage über deinen verfügbaren Urlaub (inkl. Vorgriff) hinaus verplant. Diese Tage sind noch nicht gedeckt.',
                  })}
                </p>
              </div>
            )}
            {/* Hero: Zum Planen verfügbar */}
            {zeigePlanbar && (
            <div className="rounded-2xl border-2 p-4 mb-2" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #FFFFFF)' }}>
              <p className="text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('mobilePortal.abwesend.planHero.title', 'Zum Planen verfügbar')}</p>
              <p className="text-3xl font-bold mt-1 mb-2" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                {fmt1(planbar)}{' '}
                <span className="text-sm font-normal" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('mobilePortal.abwesend.tage', 'Tage')}</span>
              </p>
              {!lightModus && (<>
              <div className="h-2.5 rounded-full overflow-hidden flex" style={{ background: 'var(--bg-inset, #F8F9FC)', border: '1px solid var(--border-card, #E5E7EB)' }}>
                <div className="h-full" style={{ width: `${pctReal}%`, background: '#338edc' }} />
                {pctVorgriff > 0 && (
                  <div className="h-full" style={{ width: `${pctVorgriff}%`, background: vorgriffPattern }} />
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#338edc' }} />
                  {t('mobilePortal.abwesend.planHero.legendeAufgebaut', { tage: fmt1(realVerfuegbar), defaultValue: '{{tage}} aus Guthaben' })}
                </span>
                <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: vorgriffPattern }} />
                  {t('mobilePortal.abwesend.planHero.legendeVorgriff', { tage: fmt1(vorgriffAnteil), defaultValue: '{{tage}} im Vorgriff' })}
                </span>
              </div>
              </>)}
              <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('mobilePortal.abwesend.planHero.hintBis', { datum: fmtDate(kontingent.urlaubsjahr_bis), defaultValue: 'bis {{datum}} zum Planen verfügbar' })}
              </p>
              {/* #1771: positiver Ausblick statt Austritts-Hinweis — beide Werte kommen aus dem Payload */}
              {naechsterUjStart(kontingent.urlaubsjahr_bis) && kontingent.anspruch != null && (
                <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                  {t('mobilePortal.abwesend.planHero.hintNeuesUj', {
                    datum: fmtDate(naechsterUjStart(kontingent.urlaubsjahr_bis)),
                    tage: fmtTage(kontingent.anspruch),
                    defaultValue: 'Ab {{datum}} kommen {{tage}} Tage zum Planen dazu',
                  })}
                </p>
              )}
            </div>
            )}
            {/* #1769: 2 Kontext-Cards — Saldo jetzt / Bereits verplant bis Ende UJ */}
            {(zeigeSaldo || zeigeVerplant) && (
            <div className={`grid ${zeigeSaldo && zeigeVerplant ? 'grid-cols-2' : 'grid-cols-1'} gap-2 mb-2`}>
              {zeigeSaldo && (
              <div className="rounded-2xl border p-2.5 text-center" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #FFFFFF)' }}>
                <p className="text-[10px] leading-tight" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('mobilePortal.abwesend.karteSaldoJetzt', 'Saldo jetzt')}</p>
                <p className="text-lg font-bold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{fmt1(standHeute)}</p>
                <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('mobilePortal.abwesend.standHeute', 'Stand heute')}</p>
              </div>
              )}
              {zeigeVerplant && (
              <div className="rounded-2xl border p-2.5 text-center" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #FFFFFF)' }}>
                <p className="text-[10px] leading-tight" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('mobilePortal.abwesend.karteVerplantUj', 'Bereits verplant')}</p>
                <p className="text-lg font-bold mt-0.5" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{fmt1(verplantUj)}</p>
                <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('mobilePortal.abwesend.bisEndeUj', { datum: fmtDate(kontingent.urlaubsjahr_bis), defaultValue: 'bis {{datum}}' })}</p>
              </div>
              )}
            </div>
            )}
            {zeigeVerplant && naechstesUj > 0 && (
              <p className="text-[10px] text-center px-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('mobilePortal.abwesend.hinweisNaechstesUj', { tage: fmt1(naechstesUj), defaultValue: '{{tage}} Tage im nächsten Urlaubsjahr' })}
              </p>
            )}
          </div>
        );
      })()}

      {/* Success Message */}
      {successMsg && (
        <div className="mx-4 mb-3 px-4 py-3 rounded-2xl bg-[#338edc]/10 border border-[#338edc]/20">
          <p className="text-sm text-[#338edc]">{successMsg}</p>
        </div>
      )}

      {/* Error Message */}
      {errorMsg && (
        <div className="mx-4 mb-3 px-4 py-3 rounded-2xl bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{errorMsg}</p>
        </div>
      )}

      {/* ─── Tab Bar ──────────────────────────────────────── */}
      <div className="mx-4 mb-4">
        <div
          className="flex rounded-[10px] p-[3px] border"
          style={{
            background: 'var(--bg-inset, #F8F9FC)',
            borderColor: 'var(--border-card, #E5E7EB)',
          }}
        >
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => switchTab(tab)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === tab
                  ? 'bg-[var(--bg-card,#fff)] shadow-sm'
                  : ''
              }`}
              style={{
                color: activeTab === tab
                  ? 'var(--text-primary, #1A1A2E)'
                  : 'var(--text-tertiary, #6B7280)',
              }}
            >
              {t(`mobilePortal.abwesend.tab_${tab}`)}
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MEINE TAB                                          */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 'meine' && (
        <div className="px-4 space-y-3">
          {loadingMeine ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : abwesenheiten.length === 0 ? (
            <div
              className="text-center py-12 rounded-2xl border"
              style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
            >
              <div className="text-3xl mb-2">📋</div>
              <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('mobilePortal.abwesend.emptyState')}
              </p>
            </div>
          ) : (
            abwesenheiten.map(item => {
              const statusStyle = STATUS_COLORS[item.status] || STATUS_COLORS.storniert;
              return (
                <div
                  key={`${item.quelle || 'a'}-${item.id}`}
                  className="rounded-2xl border p-4"
                  style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
                >
                  {/* Top row: date range + tage */}
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                      {fmtRange(item.von_datum, item.bis_datum)}
                    </span>
                    {item.tage != null && (
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{
                          background: 'var(--bg-inset, #F8F9FC)',
                          color: 'var(--text-secondary, #4B5563)',
                        }}
                      >
                        {parseFloat(item.tage) === 0.5 ? '0,5' : parseFloat(item.tage)} {t('mobilePortal.abwesend.tage')}
                      </span>
                    )}
                  </div>

                  {/* Badges row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Typ badge */}
                    <span
                      className="text-xs font-medium px-2.5 py-1 rounded-full border"
                      style={{
                        backgroundColor: (item.typ_farbe || '#6B7280') + '14',
                        borderColor: (item.typ_farbe || '#6B7280') + '30',
                        color: item.typ_farbe || '#6B7280',
                      }}
                    >
                      {item.typ_icon && (() => {
                        const IC = LucideIcons[item.typ_icon] || LucideIcons.Calendar;
                        return <IC className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />;
                      })()}
                      {item.typ_bezeichnung || item.typ || item.art_key || '—'}
                    </span>

                    {/* Status badge */}
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
                    >
                      {t(`mobilePortal.abwesend.status_${item.status}`) || item.status}
                    </span>
                  </div>

                  {/* Ablehnungsgrund */}
                  {item.status === 'abgelehnt' && item.ablehnungsgrund && (
                    <p className="mt-2 text-xs px-3 py-2 rounded-xl bg-red-50 border border-red-100 text-red-700">
                      {item.ablehnungsgrund}
                    </p>
                  )}

                  {/* Notiz */}
                  {item.notiz && (
                    <p className="mt-2 text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                      {item.notiz}
                    </p>
                  )}

                  {/* Storno button */}
                  {item.status === 'beantragt' && (
                    <button
                      onClick={() => handleStorno(item)}
                      disabled={stornoId === item.id}
                      className="mt-3 w-full min-h-[44px] rounded-2xl border text-sm font-medium transition-all active:scale-[0.98]"
                      style={{
                        borderColor: '#338edc30',
                        color: '#338edc',
                        opacity: stornoId === item.id ? 0.5 : 1,
                      }}
                    >
                      {stornoId === item.id
                        ? t('mobilePortal.abwesend.stornierend')
                        : t('mobilePortal.abwesend.stornieren')}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* ANTRAG TAB                                         */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 'antrag' && (
        <div className="px-4">
          {/* Step 1: Choose absence type */}
          {antragStep === 1 && (
            <>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('mobilePortal.abwesend.chooseType')}
              </p>

              {loadingArten ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {/* Urlaub + ZA always shown first */}
                  <button
                    onClick={() => {
                      setSelectedArt({ key: 'urlaub', label_de: 'Urlaub', label_en: 'Vacation', farbe: '#338edc', icon: 'Palmtree', braucht_genehmigung: true });
                      setAntragStep(2);
                    }}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 text-center transition-all active:scale-[0.97]"
                    style={{ borderColor: '#338edc40', background: 'var(--bg-card, #FFFFFF)' }}
                  >
                    {(() => { const IC = LucideIcons.Palmtree; return <IC className="w-7 h-7" style={{ color: '#338edc' }} />; })()}
                    <span className="text-xs font-medium leading-tight" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                      {t('mobilePortal.abwesend.typeUrlaub')}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#338edc]/10 text-[#338edc] font-medium">
                      {t('mobilePortal.abwesend.paid')}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedArt({ key: 'zeitausgleich', label_de: 'Zeitausgleich', label_en: 'Time Off', farbe: '#338edc', icon: 'Clock', braucht_genehmigung: true });
                      setHalberTag(false); setBisDatum('');
                      setAntragStep(2);
                    }}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 text-center transition-all active:scale-[0.97]"
                    style={{ borderColor: '#338edc40', background: 'var(--bg-card, #FFFFFF)' }}
                  >
                    {(() => { const IC = LucideIcons.Clock; return <IC className="w-7 h-7" style={{ color: '#338edc' }} />; })()}
                    <span className="text-xs font-medium leading-tight" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                      {t('mobilePortal.abwesend.typeZA')}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#338edc]/10 text-[#338edc] font-medium">
                      {t('mobilePortal.abwesend.paid')}
                    </span>
                  </button>

                  {/* Dynamic types from abwesenheitsarten (excluding urlaub/za) */}
                  {arten.filter(a => a.key !== 'urlaub' && a.key !== 'zeitausgleich').map(art => (
                    <button
                      key={art.id || art.key}
                      onClick={() => {
                        setSelectedArt(art);
                        setAntragStep(2);
                      }}
                      className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 text-center transition-all active:scale-[0.97]"
                      style={{
                        borderColor: '#338edc40',
                        background: 'var(--bg-card, #FFFFFF)',
                      }}
                    >
                      {(() => {
                        const IC = art.icon && LucideIcons[art.icon];
                        return IC ? <IC className="w-7 h-7" style={{ color: '#338edc' }} /> : (() => { const Fb = LucideIcons.Calendar; return <Fb className="w-7 h-7" style={{ color: '#338edc' }} />; })();
                      })()}
                      <span
                        className="text-xs font-medium leading-tight"
                        style={{ color: 'var(--text-primary, #1A1A2E)' }}
                      >
                        {art.label_de || art.bezeichnung_de || art.key}
                      </span>
                      {art.bezahlt != null && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          art.bezahlt ? 'bg-[#338edc]/10 text-[#338edc]' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]'
                        }`}>
                          {art.bezahlt ? t('mobilePortal.abwesend.paid') : t('mobilePortal.abwesend.unpaid')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Step 2: Date selection + submit */}
          {antragStep === 2 && selectedArt && (
            <form onSubmit={handleSubmitAntrag} className="space-y-4">
              {/* Back + selected type */}
              <div className="flex items-center gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => {
                    setAntragStep(1);
                    setSelectedArt(null);
                    setVonDatum('');
                    setBisDatum('');
                    setHalberTag(false);
                    setGrund('');
                    setUploadFile(null);
                    setErrorMsg('');
                    setProjectedSaldo(null);
                  }}
                  className="w-9 h-9 rounded-full flex items-center justify-center border"
                  style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  {(() => {
                    const IC = (selectedArt.icon && LucideIcons[selectedArt.icon]) || LucideIcons.Calendar;
                    return <IC className="w-6 h-6" style={{ color: '#338edc' }} />;
                  })()}
                  <div>
                    <span className="font-medium text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                      {selectedArt.label_de || selectedArt.bezeichnung_de || selectedArt.key}
                    </span>
                    {selectedArt.braucht_genehmigung && (
                      <p className="text-[10px]" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                        {t('mobilePortal.abwesend.needsApproval')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Krankenstand: Simplified form (no required bis-datum) ── */}
              {isKrankenstandArt(selectedArt.key) ? (
                <>
                  {/* Info banner */}
                  <div
                    className="rounded-2xl px-4 py-3"
                    style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}
                  >
                    <p className="text-sm font-medium" style={{ color: '#991B1B' }}>
                      {t('mobilePortal.abwesend.sickToday', 'Ab heute krank')}
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
                      {t('mobilePortal.abwesend.sickNoApproval', 'Keine Genehmigung nötig. HR wird automatisch informiert.')}
                    </p>
                  </div>

                  {/* Duration radio */}
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                      {t('mobilePortal.abwesend.expectedDuration', 'Voraussichtliche Dauer')}
                    </label>
                    <div className="space-y-2">
                      <label
                        className={`flex items-center gap-3 min-h-[44px] px-4 rounded-2xl border cursor-pointer transition-colors ${
                          krankDauer === 'unbekannt' ? 'border-[#338edc]' : ''
                        }`}
                        style={{
                          borderColor: krankDauer === 'unbekannt' ? '#338edc' : 'var(--border-card, #E5E7EB)',
                          background: krankDauer === 'unbekannt' ? '#338edc08' : 'var(--bg-card, #FFFFFF)',
                        }}
                      >
                        <input
                          type="radio"
                          name="krankDauer"
                          checked={krankDauer === 'unbekannt'}
                          onChange={() => { setKrankDauer('unbekannt'); setBisDatum(''); }}
                          className="accent-[#338edc]"
                        />
                        <span className="text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {t('mobilePortal.abwesend.durationUnknown', 'Unbekannt')}
                        </span>
                      </label>
                      <label
                        className={`flex items-center gap-3 min-h-[44px] px-4 rounded-2xl border cursor-pointer transition-colors ${
                          krankDauer === 'bis' ? 'border-[#338edc]' : ''
                        }`}
                        style={{
                          borderColor: krankDauer === 'bis' ? '#338edc' : 'var(--border-card, #E5E7EB)',
                          background: krankDauer === 'bis' ? '#338edc08' : 'var(--bg-card, #FFFFFF)',
                        }}
                      >
                        <input
                          type="radio"
                          name="krankDauer"
                          checked={krankDauer === 'bis'}
                          onChange={() => setKrankDauer('bis')}
                          className="accent-[#338edc]"
                        />
                        <span className="text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {t('mobilePortal.abwesend.durationUntil', 'Bis voraussichtlich')}
                        </span>
                      </label>
                    </div>
                    {krankDauer === 'bis' && (
                      <input
                        type="date"
                        value={bisDatum}
                        onChange={e => setBisDatum(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                        className="w-full min-h-[48px] px-4 mt-2 rounded-2xl border text-sm"
                        style={{
                          borderColor: 'var(--border-card, #E5E7EB)',
                          background: 'var(--bg-card, #FFFFFF)',
                          color: 'var(--text-primary, #1A1A2E)',
                        }}
                      />
                    )}
                  </div>

                  {/* File upload for Krankenstand */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                      {t('mobilePortal.abwesend.medicalCert')}
                    </label>
                    <label
                      className="flex items-center gap-3 min-h-[48px] px-4 rounded-2xl border cursor-pointer transition-colors"
                      style={{
                        borderColor: uploadFile ? '#338edc' : 'var(--border-card, #E5E7EB)',
                        background: uploadFile ? '#338edc08' : 'var(--bg-card, #FFFFFF)',
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={uploadFile ? '#338edc' : '#9CA3AF'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span className="flex-1 text-sm truncate" style={{ color: uploadFile ? 'var(--text-primary, #1A1A2E)' : 'var(--text-muted, #9CA3AF)' }}>
                        {uploadFile ? uploadFile.name : t('mobilePortal.abwesend.selectFile')}
                      </span>
                      {uploadFile && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setUploadFile(null); }}
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ color: 'var(--text-tertiary, #6B7280)' }}
                        >
                          ✕
                        </button>
                      )}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={e => setUploadFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                      {t('mobilePortal.abwesend.medicalCertHint')}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* ── Standard form: Urlaub/ZA/Other ── */}

                  {/* Half-day toggle (#1546 — nur Urlaub, NICHT Zeitausgleich) */}
                  {selectedArt.key === 'urlaub' && (
                    <div
                      className="flex rounded-[10px] p-[3px] border"
                      style={{ background: 'var(--bg-inset, #F8F9FC)', borderColor: 'var(--border-card, #E5E7EB)' }}
                    >
                      <button
                        type="button"
                        onClick={() => { setHalberTag(false); setBisDatum(''); }}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${!halberTag ? 'bg-[var(--bg-card,#fff)] shadow-sm' : ''}`}
                        style={{ color: !halberTag ? 'var(--text-primary, #1A1A2E)' : 'var(--text-tertiary, #6B7280)' }}
                      >
                        {t('mobilePortal.abwesend.fullDay')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setHalberTag(true); setBisDatum(vonDatum); }}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${halberTag ? 'bg-[var(--bg-card,#fff)] shadow-sm' : ''}`}
                        style={{ color: halberTag ? 'var(--text-primary, #1A1A2E)' : 'var(--text-tertiary, #6B7280)' }}
                      >
                        {t('mobilePortal.abwesend.halfDay')}
                      </button>
                    </div>
                  )}

                  {/* Von-Datum */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                      {halberTag ? t('mobilePortal.abwesend.datum') : t('mobilePortal.abwesend.vonDatum')} *
                    </label>
                    <input
                      type="date"
                      value={vonDatum}
                      onChange={e => { setVonDatum(e.target.value); if (halberTag) setBisDatum(e.target.value); }}
                      required
                      className="w-full min-h-[48px] px-4 rounded-2xl border text-sm"
                      style={{
                        borderColor: 'var(--border-card, #E5E7EB)',
                        background: 'var(--bg-card, #FFFFFF)',
                        color: 'var(--text-primary, #1A1A2E)',
                      }}
                    />
                  </div>

                  {/* Bis-Datum (hidden for half-day) */}
                  {!halberTag && (
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                        {t('mobilePortal.abwesend.bisDatum')}
                      </label>
                      <input
                        type="date"
                        value={bisDatum}
                        onChange={e => setBisDatum(e.target.value)}
                        min={vonDatum || undefined}
                        className="w-full min-h-[48px] px-4 rounded-2xl border text-sm"
                        style={{
                          borderColor: 'var(--border-card, #E5E7EB)',
                          background: 'var(--bg-card, #FFFFFF)',
                          color: 'var(--text-primary, #1A1A2E)',
                        }}
                      />
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                        {t('mobilePortal.abwesend.bisDatumHint')}
                      </p>
                    </div>
                  )}

                  {/* Arbeitstage preview + Saldo-Prognose */}
                  {vonDatum && arbeitstage != null && (
                    <div
                      className="rounded-2xl border px-4 py-3 space-y-2"
                      style={{
                        borderColor: arbeitstage === 0 ? '#F59E0B' : 'var(--border-card, #E5E7EB)',
                        background: arbeitstage === 0 ? '#FFFBEB' : 'var(--bg-inset, #F8F9FC)',
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                          {t('mobilePortal.abwesend.arbeitstage')}
                        </span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {arbeitstage === 0.5 ? '0,5' : arbeitstage} {t('mobilePortal.abwesend.tage')}
                        </span>
                      </div>
                      {arbeitstage === 0 && (
                        <p className="text-xs" style={{ color: '#92400E' }}>
                          {t('mobilePortal.abwesend.keineArbeitstage')}
                        </p>
                      )}

                      {/* Saldo-Prognose for Urlaub/ZA */}
                      {projectedSaldo && isUrlaubType(selectedArt?.key) && arbeitstage > 0 && (() => {
                        // #1737: planbar inkl. dynamischem Vorgriff — konsistent zur BE-Antragsvalidierung
                        const aktuellAm = Number(projectedSaldo.verfuegbar_planbar ?? projectedSaldo.aktuell_verfuegbar ?? projectedSaldo.saldo ?? 0);
                        // #1698: Preview basiert auf der UJ-Prognose (JE-Kachel entfernt)
                        const prognoseBasis = Number(projectedSaldo.prognose_urlaubsjahr ?? projectedSaldo.projiziert_jahresende ?? projectedSaldo.saldo ?? 0);
                        const nachAntrag = prognoseBasis - arbeitstage;
                        return (
                          <>
                            <div className="border-t pt-2" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
                              <div className="flex items-center justify-between">
                                <span className="text-xs" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                                  {t('mobilePortal.abwesend.aufgebautBis', { datum: fmtDate(vonDatum) })}
                                </span>
                                <span className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                                  {parseFloat(projectedSaldo.aufgebaut || 0).toFixed(1)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                                  {t('mobilePortal.abwesend.aktuellVerfuegbar', 'Aktuell verfügbar')}
                                </span>
                                <span className="text-sm font-bold" style={{ color: aktuellAm < 0 ? '#DC2626' : 'var(--text-primary, #1A1A2E)' }}>
                                  {Math.round(aktuellAm * 10) / 10}
                                </span>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                                  {t('mobilePortal.abwesend.nachAntrag', 'Nach Antrag')}
                                </span>
                                <span className="text-sm font-bold" style={{ color: nachAntrag < 0 ? '#DC2626' : '#059669' }}>
                                  {Math.round(nachAntrag * 10) / 10}
                                </span>
                              </div>
                            </div>
                            {nachAntrag >= 0 && (
                              <p className="text-xs text-green-600">
                                {t('mobilePortal.abwesend.saldoOk')}
                              </p>
                            )}
                            {nachAntrag < 0 && (
                              <p className="text-xs text-red-600">
                                {t('mobilePortal.abwesend.saldoInsufficient')}
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* Grund / Notiz */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {t('mobilePortal.abwesend.grund')}
                </label>
                <textarea
                  value={grund}
                  onChange={e => setGrund(e.target.value)}
                  rows={3}
                  placeholder={t('mobilePortal.abwesend.grundPlaceholder')}
                  className="w-full px-4 py-3 rounded-2xl border text-sm resize-none"
                  style={{
                    borderColor: 'var(--border-card, #E5E7EB)',
                    background: 'var(--bg-card, #FFFFFF)',
                    color: 'var(--text-primary, #1A1A2E)',
                  }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || (!isKrankenstandArt(selectedArt?.key) && (!vonDatum || arbeitstage === 0))}
                className="w-full min-h-[48px] rounded-2xl text-sm font-semibold text-white transition-all active:scale-[0.98]"
                style={{
                  background: submitting || (!isKrankenstandArt(selectedArt?.key) && (!vonDatum || arbeitstage === 0)) ? '#9CA3AF' : isKrankenstandArt(selectedArt?.key) ? '#DC2626' : '#338edc',
                }}
              >
                {submitting
                  ? t('mobilePortal.abwesend.submitting')
                  : isKrankenstandArt(selectedArt?.key)
                    ? t('mobilePortal.abwesend.submitKrank', 'Krank melden')
                    : t('mobilePortal.abwesend.submitAntrag')}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* TEAM TAB                                           */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 'team' && (
        <div className="px-4">
          {/* Month navigation */}
          <div
            className="flex items-center justify-between mb-4 rounded-2xl border px-4 py-3"
            style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
          >
            <button
              onClick={prevMonth}
              className="w-9 h-9 rounded-full flex items-center justify-center border"
              style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {new Date(teamMonth.year, teamMonth.month - 1).toLocaleDateString('de-AT', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={nextMonth}
              className="w-9 h-9 rounded-full flex items-center justify-center border"
              style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          {loadingTeam ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Calendar grid */}
              <div
                className="rounded-2xl border overflow-hidden mb-4"
                style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
              >
                {/* Weekday headers */}
                <div className="grid grid-cols-7">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                    <div
                      key={d}
                      className="text-center text-[10px] font-medium py-2"
                      style={{
                        color: 'var(--text-tertiary, #6B7280)',
                        background: 'var(--bg-inset, #F8F9FC)',
                      }}
                    >
                      {d}
                    </div>
                  ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7">
                  {(() => {
                    const daysInMonth = getDaysInMonth(teamMonth.year, teamMonth.month);
                    const firstDay = getFirstDayOfWeek(teamMonth.year, teamMonth.month);
                    const cells = [];
                    const today = new Date();
                    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                    for (let i = 0; i < firstDay; i++) {
                      cells.push(<div key={`empty-${i}`} className="h-12" />);
                    }

                    for (let d = 1; d <= daysInMonth; d++) {
                      const dateStr = `${toMonthStr(teamMonth.year, teamMonth.month)}-${String(d).padStart(2, '0')}`;
                      const dayData = teamData[dateStr];
                      const isToday = dateStr === todayStr;
                      const isWeekend = (() => {
                        const dow = new Date(teamMonth.year, teamMonth.month - 1, d).getDay();
                        return dow === 0 || dow === 6;
                      })();

                      cells.push(
                        <div
                          key={d}
                          className="h-12 flex flex-col items-center justify-center relative"
                          style={{
                            background: isWeekend ? 'var(--bg-inset, #F8F9FC)' : 'transparent',
                          }}
                        >
                          <span
                            className={`text-xs font-medium leading-none ${isToday ? 'w-6 h-6 flex items-center justify-center rounded-full' : ''}`}
                            style={{
                              color: isWeekend
                                ? 'var(--text-muted, #9CA3AF)'
                                : 'var(--text-primary, #1A1A2E)',
                              ...(isToday ? { background: '#338edc', color: '#FFFFFF' } : {}),
                            }}
                          >
                            {d}
                          </span>

                          {dayData && dayData.anzahl > 0 && (
                            <div className="flex gap-0.5 mt-0.5">
                              {dayData.personen && dayData.personen.length > 0 ? (
                                dayData.personen.slice(0, 3).map((p, idx) => (
                                  <div
                                    key={idx}
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: getTypColor(p.typ) }}
                                  />
                                ))
                              ) : (
                                Array.from({ length: Math.min(dayData.anzahl, 3) }).map((_, idx) => (
                                  <div
                                    key={idx}
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: TEAM_COLORS.urlaub }}
                                  />
                                ))
                              )}
                              {(dayData.anzahl > 3) && (
                                <span className="text-[8px] leading-none" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                                  +{dayData.anzahl - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return cells;
                  })()}
                </div>
              </div>

              {/* Legend */}
              <div
                className="rounded-2xl border p-4"
                style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
              >
                <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {t('mobilePortal.abwesend.legende')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'urlaub', label: t('mobilePortal.abwesend.legendUrlaub'), color: TEAM_COLORS.urlaub },
                    { key: 'krank', label: t('mobilePortal.abwesend.legendKrank'), color: TEAM_COLORS.krankenstand },
                    { key: 'za', label: t('mobilePortal.abwesend.legendZA'), color: TEAM_COLORS.zeitausgleich },
                    { key: 'sonstige', label: t('mobilePortal.abwesend.legendSonstige'), color: TEAM_COLORS.sonstige },
                  ].map(item => (
                    <div key={item.key} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
